/* Landing page i18n.

Default language is English. Russian is last in the picker.
Adding a language: append it to LANGS, copy a block, translate values.
Placeholders use {n} substitution.
*/
export const DEFAULT_LANG = "en";

// Order shown in the picker. Keep `ru` last per product preference.
export const LANGS = ["en", "es", "de", "fr", "tr", "pt", "ru"];

export const LANG_LABELS = {
  en: "English",
  es: "Español",
  de: "Deutsch",
  fr: "Français",
  tr: "Türkçe",
  pt: "Português",
  ru: "Русский",
};

export const LANG_FLAGS = {
  en: "🇬🇧",
  es: "🇪🇸",
  de: "🇩🇪",
  fr: "🇫🇷",
  tr: "🇹🇷",
  pt: "🇵🇹",
  ru: "🇷🇺",
};

export function detectLang() {
  if (typeof window === "undefined") return DEFAULT_LANG;
  const stored = window.localStorage.getItem("clipra.lang");
  if (stored && LANGS.includes(stored)) return stored;
  const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
  return LANGS.includes(nav) ? nav : DEFAULT_LANG;
}

export const STRINGS = {
  en: {
    "nav.lang": "Language",
    "nav.admin": "Admin",
    "nav.openBot": "Open Telegram bot",

    "hero.eyebrow": "// video tools · v1.0",
    "hero.title.line1": "Cut and reframe videos for shorts.",
    "hero.title.line2_prefix": "In one Telegram ",
    "hero.title.swap_a": "bot",
    "hero.title.swap_b": "app",
    "hero.title.line2_suffix": ".",
    "hero.subtitle":
      "Paste a link to your own video and turn it into a share-ready vertical clip. Trim the moment you need, reframe it for mobile screens — all inside Telegram, no apps to install.",
    "hero.cta.primary": "Open in Telegram",
    "hero.cta.secondary": "How it works",

    "feat.title": "What Clipra does",
    "feat.subtitle": "Everything below is live in production today.",

    "feat.download.title": "Download videos by link",
    "feat.download.body":
      "1,000+ sites supported. YouTube, Instagram Reels, TikTok, X, VK, Facebook, Vimeo and more.",
    "feat.download.tag": "live",

    "feat.cut.title": "Cut a fragment by timecode",
    "feat.cut.body":
      "Tell the bot the seconds you want — it returns just the clip. Fast, no re-encoding.",
    "feat.cut.tag": "live",

    "feat.miniapp.title": "Mini App editor",
    "feat.miniapp.body":
      "A visual editor inside Telegram with a scrubbable timeline, frame-by-frame ribbon and live preview. No app to install.",
    "feat.miniapp.tag": "live",

    "feat.refine.title": "Smart Refine — instant re-edits",
    "feat.refine.body":
      "Move the cut boundaries by 0.1s and re-export in seconds — no re-download. Pro gets the full source pre-cached so you can refine anywhere on the timeline.",
    "feat.refine.tag": "live",

    "feat.library.title": "Personal Library",
    "feat.library.body":
      "Save the clips you want to keep tweaking. Free keeps 10 for 14 days; Pro keeps 50 with no expiry.",
    "feat.library.tag": "live",

    "feat.history.title": "History & quotas",
    "feat.history.body":
      "Every job is one tap away — preview thumbnails, share, refine or save to library. Free keeps 5 days, Pro keeps 30.",
    "feat.history.tag": "live",

    "feat.langs.title": "Speaks your language",
    "feat.langs.body":
      "Mini App and bot ship in 7 languages and auto-detect yours. Switch any time from the header.",
    "feat.langs.tag": "live",

    "feat.large.title": "Files of any size",
    "feat.large.body":
      "Files over 50 MB on Free — a short download link valid for 24 hours. Pro receives files up to 2 GB straight in chat.",
    "feat.large.tag": "live",

    "feat.precise.title": "Frame-precise cutting",
    "feat.precise.body":
      "Trim down to 0.1 second with re-encoding. Perfect for clips, Reels and meme moments.",
    "feat.precise.tag": "pro",

    "feat.crop.title": "Crop the frame",
    "feat.crop.body":
      "Repurpose horizontal videos for vertical platforms. Presets 9:16 / 1:1 / 4:5 plus freeform — drag the box to pick the composition.",
    "feat.crop.tag": "pro",

    "feat.subs.title": "Pro via Telegram Stars",
    "feat.subs.body":
      "800 Stars / month, paid right inside the chat. No card numbers, no external checkout, cancel any time from Telegram settings.",
    "feat.subs.tag": "pro",

    "tag.live": "live",
    "tag.pro": "Pro",
    "tag.soon": "coming soon",

    "how.title": "How it works",
    "how.step1.title": "Send a link",
    "how.step1.body": "Paste any video URL into the chat with @Clipra_bot.",
    "how.step2.title": "Pick what to do",
    "how.step2.body": "Open the Mini App for visual editing, or just type seconds for a quick cut.",
    "how.step3.title": "Get your file",
    "how.step3.body": "We return the video right in the chat — or a clean download link if it's huge.",

    "cta.title": "Ready to clip?",
    "cta.body": "Free to start. No sign-up. Just open Telegram.",
    "cta.button": "Start the bot",

    "foot.tagline": "// video tools, made small",
    "foot.terms": "Terms of Service",
    "foot.copyright": "© Clipra · All rights reserved",
  },

  es: {
    "nav.lang": "Idioma",
    "nav.admin": "Admin",
    "nav.openBot": "Abrir bot de Telegram",
    "hero.eyebrow": "// herramientas de vídeo · v1.0",
    "hero.title.line1": "Corta y reencuadra vídeos para shorts.",
    "hero.title.line2_prefix": "En un solo Telegram-",
    "hero.title.swap_a": "bot",
    "hero.title.swap_b": "app",
    "hero.title.line2_suffix": ".",
    "hero.subtitle":
      "Pega un enlace a tu propio vídeo y conviértelo en un clip vertical listo para compartir. Recorta el momento que necesitas y reencuadralo para pantallas móviles — todo dentro de Telegram, sin apps que instalar.",
    "hero.cta.primary": "Abrir en Telegram",
    "hero.cta.secondary": "Cómo funciona",
    "feat.title": "Qué hace Clipra",
    "feat.subtitle": "Todo lo de abajo funciona hoy, o está en la hoja de ruta pública.",
    "feat.download.title": "Descarga vídeos por enlace",
    "feat.download.body":
      "Más de 1.000 sitios. YouTube, Instagram Reels, TikTok, X, VK, Facebook, Vimeo y más.",
    "feat.download.tag": "live",
    "feat.cut.title": "Corta un fragmento por código de tiempo",
    "feat.cut.body":
      "Dile al bot los segundos exactos — te devuelve solo el clip. Rápido, sin recodificación.",
    "feat.cut.tag": "live",
    "feat.refine.title": "Smart Refine — re-edits al instante",
    "feat.refine.body":
      "Mueve los límites del corte en 0,1 s y reexporta en segundos — sin volver a descargar. Pro tiene la fuente completa precacheada para refinar en cualquier punto del timeline.",
    "feat.refine.tag": "live",
    "feat.library.title": "Personal Library",
    "feat.library.body":
      "Guarda los clips que quieras seguir afinando. Free guarda 10 durante 14 días; Pro guarda 50 sin caducidad.",
    "feat.library.tag": "live",
    "feat.large.title": "Archivos de cualquier tamaño",
    "feat.large.body":
      "Archivos de más de 50 MB en Free — un enlace de descarga corto válido 24 horas. Pro recibe archivos de hasta 2 GB directamente en el chat.",
    "feat.large.tag": "live",
    "feat.langs.title": "Habla tu idioma",
    "feat.langs.body":
      "El bot viene en 7 idiomas y detecta el tuyo. Cámbialo cuando quieras con /lang.",
    "feat.langs.tag": "live",
    "feat.precise.title": "Corte con precisión de frame",
    "feat.precise.body":
      "Recorta hasta 0,1 s con recodificación. Perfecto para clips y reels.",
    "feat.precise.tag": "pro",
    "feat.crop.title": "Recorta el frame",
    "feat.crop.body":
      "Adapta vídeos horizontales para plataformas verticales con un toque.",
    "feat.crop.tag": "pro",
    "feat.recut.title": "Re-cortar sin volver a descargar",
    "feat.recut.body":
      "Cambia los límites al instante. La fuente queda en caché durante la sesión.",
    "feat.recut.tag": "pro",
    "feat.miniapp.title": "Editor Mini App",
    "feat.miniapp.body":
      "Editor visual dentro de Telegram con timeline, scrubber y vista previa. Sin instalar nada.",
    "feat.miniapp.tag": "live",
    "feat.subs.title": "Pro con Telegram Stars",
    "feat.subs.body":
      "Paga con Stars dentro del chat. Sin números de tarjeta, sin checkouts externos.",
    "feat.subs.tag": "pro",
    "feat.priority.title": "Cola prioritaria",
    "feat.priority.body":
      "Los usuarios Pro saltan la fila en horas punta. La espera media se mantiene por debajo de 10 s.",
    "feat.priority.tag": "soon",
    "feat.history.title": "Historial y cuotas",
    "feat.history.body":
      "Tus últimas tareas, límites diarios y posibilidad de reejecutar cualquiera con un toque.",
    "feat.history.tag": "live",
    "tag.live": "disponible",
    "tag.pro": "Pro",
    "tag.soon": "próximamente",
    "how.title": "Cómo funciona",
    "how.step1.title": "Envía un enlace",
    "how.step1.body": "Pega cualquier URL de vídeo en el chat con @Clipra_bot.",
    "how.step2.title": "Elige qué hacer",
    "how.step2.body":
      "Descarga el archivo completo o indica los segundos para un fragmento.",
    "how.step3.title": "Recibe tu archivo",
    "how.step3.body":
      "Te devolvemos el vídeo en el chat — o un enlace de descarga limpio.",
    "cta.title": "¿Listo para cortar?",
    "cta.body": "Gratis para empezar. Sin registro. Solo abre Telegram.",
    "cta.button": "Iniciar el bot",
    "foot.tagline": "// herramientas de vídeo, hechas pequeñas",
    "foot.terms": "Términos del servicio",
    "foot.copyright": "© Clipra · Todos los derechos reservados",
  },

  de: {
    "nav.lang": "Sprache",
    "nav.admin": "Admin",
    "nav.openBot": "Telegram-Bot öffnen",
    "hero.eyebrow": "// video-tools · v1.0",
    "hero.title.line1": "Schneide und reframe Videos für Shorts.",
    "hero.title.line2_prefix": "In einem Telegram-",
    "hero.title.swap_a": "Bot",
    "hero.title.swap_b": "App",
    "hero.title.line2_suffix": ".",
    "hero.subtitle":
      "Füge einen Link zu deinem eigenen Video ein und mache daraus einen teilbaren vertikalen Clip. Schneide den gewünschten Moment heraus und passe ihn ans Handy-Format an — alles in Telegram, ohne App-Installation.",
    "hero.cta.primary": "In Telegram öffnen",
    "hero.cta.secondary": "So funktioniert's",
    "feat.title": "Was Clipra kann",
    "feat.subtitle": "Alles unten funktioniert heute oder ist auf der öffentlichen Roadmap.",
    "feat.download.title": "Videos per Link laden",
    "feat.download.body":
      "Über 1.000 Seiten. YouTube, Instagram Reels, TikTok, X, VK, Facebook, Vimeo und mehr.",
    "feat.download.tag": "live",
    "feat.cut.title": "Ausschnitt per Timecode",
    "feat.cut.body":
      "Sag dem Bot die exakten Sekunden — du bekommst nur den Clip. Schnell, ohne Recoding.",
    "feat.cut.tag": "live",
    "feat.refine.title": "Smart Refine — sofortige Korrekturen",
    "feat.refine.body":
      "Verschiebe die Schnittgrenzen um 0,1 s und exportiere in Sekunden neu — kein erneuter Download. Pro hat die volle Quelle vorab im Cache, um an jeder Stelle der Timeline zu schneiden.",
    "feat.refine.tag": "live",
    "feat.library.title": "Personal Library",
    "feat.library.body":
      "Speichere Clips, die du weiter feinjustieren willst. Free behält 10 Stück für 14 Tage; Pro behält 50 ohne Ablauf.",
    "feat.library.tag": "live",
    "feat.large.title": "Dateien jeder Größe",
    "feat.large.body":
      "Dateien über 50 MB im Free-Plan — ein kurzer Download-Link, 24 Stunden gültig. Pro empfängt Dateien bis 2 GB direkt im Chat.",
    "feat.large.tag": "live",
    "feat.langs.title": "Spricht deine Sprache",
    "feat.langs.body":
      "Der Bot kommt in 7 Sprachen und erkennt deine automatisch. Mit /lang jederzeit wechseln.",
    "feat.langs.tag": "live",
    "feat.precise.title": "Frame-genauer Schnitt",
    "feat.precise.body":
      "Trimmt auf 0,1 Sekunde mit Recoding. Perfekt für Clips und Reels.",
    "feat.precise.tag": "pro",
    "feat.crop.title": "Bildausschnitt zuschneiden",
    "feat.crop.body":
      "Mach aus horizontalen Videos vertikale für mobile Plattformen — mit einem Tipp.",
    "feat.crop.tag": "pro",
    "feat.recut.title": "Neu schneiden ohne neuen Download",
    "feat.recut.body":
      "Ändere Grenzen sofort. Die Quelle bleibt in deinem Cache für die Sitzung.",
    "feat.recut.tag": "pro",
    "feat.miniapp.title": "Mini-App-Editor",
    "feat.miniapp.body":
      "Visueller Editor in Telegram mit Timeline, Scrubber und Vorschau. Keine App-Installation.",
    "feat.miniapp.tag": "live",
    "feat.subs.title": "Pro via Telegram Stars",
    "feat.subs.body":
      "Bezahle direkt im Chat mit Stars. Keine Kartennummern, kein externer Checkout.",
    "feat.subs.tag": "pro",
    "feat.priority.title": "Priorisierte Warteschlange",
    "feat.priority.body":
      "Pro-Nutzer überspringen Stoßzeiten. Durchschnittliche Wartezeit unter 10 Sekunden.",
    "feat.priority.tag": "soon",
    "feat.history.title": "Verlauf & Kontingente",
    "feat.history.body":
      "Deine letzten Aufträge, Tageslimits und Re-run mit einem Tipp.",
    "feat.history.tag": "live",
    "tag.live": "verfügbar",
    "tag.pro": "Pro",
    "tag.soon": "demnächst",
    "how.title": "So funktioniert's",
    "how.step1.title": "Link senden",
    "how.step1.body": "Füge eine beliebige Video-URL im Chat mit @Clipra_bot ein.",
    "how.step2.title": "Aktion wählen",
    "how.step2.body":
      "Komplett laden oder Sekunden für einen Ausschnitt angeben.",
    "how.step3.title": "Datei erhalten",
    "how.step3.body":
      "Wir senden das Video zurück in den Chat — oder einen sauberen Download-Link.",
    "cta.title": "Bereit zu clippen?",
    "cta.body": "Kostenlos starten. Keine Anmeldung. Einfach Telegram öffnen.",
    "cta.button": "Bot starten",
    "foot.tagline": "// video-tools, kompakt gemacht",
    "foot.terms": "Nutzungsbedingungen",
    "foot.copyright": "© Clipra · Alle Rechte vorbehalten",
  },

  fr: {
    "nav.lang": "Langue",
    "nav.admin": "Admin",
    "nav.openBot": "Ouvrir le bot Telegram",
    "hero.eyebrow": "// outils vidéo · v1.0",
    "hero.title.line1": "Coupe et recadre des vidéos pour les shorts.",
    "hero.title.line2_prefix": "Dans un seul Telegram-",
    "hero.title.swap_a": "bot",
    "hero.title.swap_b": "app",
    "hero.title.line2_suffix": ".",
    "hero.subtitle":
      "Colle un lien vers ta propre vidéo et transforme-la en un clip vertical prêt à partager. Coupe le moment voulu et recadre-le pour l'écran mobile — tout dans Telegram, sans rien à installer.",
    "hero.cta.primary": "Ouvrir dans Telegram",
    "hero.cta.secondary": "Comment ça marche",
    "feat.title": "Ce que fait Clipra",
    "feat.subtitle": "Tout ci-dessous est dispo aujourd'hui ou sur la feuille de route publique.",
    "feat.download.title": "Téléchargement par lien",
    "feat.download.body":
      "Plus de 1 000 sites. YouTube, Instagram Reels, TikTok, X, VK, Facebook, Vimeo et plus.",
    "feat.download.tag": "live",
    "feat.cut.title": "Découpe par timecode",
    "feat.cut.body":
      "Donne au bot les secondes exactes — tu reçois juste le clip. Rapide, sans réencodage.",
    "feat.cut.tag": "live",
    "feat.refine.title": "Smart Refine — retouches instantanées",
    "feat.refine.body":
      "Décale les bords du clip de 0,1 s et ré-exporte en secondes — sans nouveau téléchargement. Pro a la source complète pré-mise en cache pour retoucher n'importe où sur la timeline.",
    "feat.refine.tag": "live",
    "feat.library.title": "Personal Library",
    "feat.library.body":
      "Sauvegarde les clips à peaufiner. Free garde 10 pendant 14 jours ; Pro garde 50 sans expiration.",
    "feat.library.tag": "live",
    "feat.large.title": "Fichiers de toute taille",
    "feat.large.body":
      "Fichiers de plus de 50 Mo en Free — un lien de téléchargement court valable 24 h. Pro reçoit des fichiers jusqu'à 2 Go directement dans le chat.",
    "feat.large.tag": "live",
    "feat.langs.title": "Parle ta langue",
    "feat.langs.body":
      "Le bot existe en 7 langues et détecte la tienne. Change quand tu veux avec /lang.",
    "feat.langs.tag": "live",
    "feat.precise.title": "Découpe au frame près",
    "feat.precise.body":
      "Coupe à 0,1 s près avec réencodage. Parfait pour clips et reels.",
    "feat.precise.tag": "pro",
    "feat.crop.title": "Recadrer l'image",
    "feat.crop.body":
      "Transforme des vidéos horizontales pour les plateformes verticales en un tap.",
    "feat.crop.tag": "pro",
    "feat.recut.title": "Re-couper sans retélécharger",
    "feat.recut.body":
      "Change les bornes instantanément. La source reste en cache pour la session.",
    "feat.recut.tag": "pro",
    "feat.miniapp.title": "Éditeur Mini App",
    "feat.miniapp.body":
      "Éditeur visuel dans Telegram avec timeline, scrubber et preview. Sans installation.",
    "feat.miniapp.tag": "live",
    "feat.subs.title": "Pro avec Telegram Stars",
    "feat.subs.body":
      "Paie en Stars dans le chat. Sans numéro de carte, sans checkout externe.",
    "feat.subs.tag": "pro",
    "feat.priority.title": "File prioritaire",
    "feat.priority.body":
      "Les Pro passent devant aux heures de pointe. Attente moyenne sous 10 secondes.",
    "feat.priority.tag": "soon",
    "feat.history.title": "Historique & quotas",
    "feat.history.body":
      "Tes dernières tâches, quotas quotidiens et re-run en un tap.",
    "feat.history.tag": "live",
    "tag.live": "disponible",
    "tag.pro": "Pro",
    "tag.soon": "bientôt",
    "how.title": "Comment ça marche",
    "how.step1.title": "Envoie un lien",
    "how.step1.body": "Colle n'importe quelle URL vidéo dans le chat avec @Clipra_bot.",
    "how.step2.title": "Choisis l'action",
    "how.step2.body":
      "Télécharge le fichier complet, ou précise les secondes pour un fragment.",
    "how.step3.title": "Récupère ton fichier",
    "how.step3.body":
      "On renvoie la vidéo dans le chat — ou un lien de téléchargement propre.",
    "cta.title": "Prêt à clipper ?",
    "cta.body": "Gratuit. Sans inscription. Ouvre Telegram, c'est tout.",
    "cta.button": "Démarrer le bot",
    "foot.tagline": "// outils vidéo, faits petits",
    "foot.terms": "Conditions d'utilisation",
    "foot.copyright": "© Clipra · Tous droits réservés",
  },

  tr: {
    "nav.lang": "Dil",
    "nav.admin": "Yönetim",
    "nav.openBot": "Telegram botunu aç",
    "hero.eyebrow": "// video araçları · v1.0",
    "hero.title.line1": "Shorts için video kes ve yeniden çerçevele.",
    "hero.title.line2_prefix": "Tek bir Telegram-",
    "hero.title.swap_a": "bot",
    "hero.title.swap_b": "app",
    "hero.title.line2_suffix": "'unda.",
    "hero.subtitle":
      "Kendi videonun bağlantısını yapıştır ve paylaşıma hazır dikey klibe dönüştür. İhtiyacın olan anı kes, mobil ekrana göre yeniden çerçevele — hepsi Telegram içinde, kuracak uygulama yok.",
    "hero.cta.primary": "Telegram'da aç",
    "hero.cta.secondary": "Nasıl çalışır",
    "feat.title": "Clipra ne yapar",
    "feat.subtitle": "Aşağıdakilerin tamamı ya bugün çalışıyor ya da herkese açık yol haritasında.",
    "feat.download.title": "Bağlantıyla video indir",
    "feat.download.body":
      "1.000+ site destekleniyor. YouTube, Instagram Reels, TikTok, X, VK, Facebook, Vimeo ve dahası.",
    "feat.download.tag": "live",
    "feat.cut.title": "Zaman koduyla parça kes",
    "feat.cut.body":
      "Bota tam saniyeleri söyle — sana sadece klibi verir. Hızlı, yeniden kodlama yok.",
    "feat.cut.tag": "live",
    "feat.refine.title": "Smart Refine — anlık düzeltmeler",
    "feat.refine.body":
      "Kesim sınırlarını 0,1 sn kaydır ve saniyeler içinde yeniden dışa aktar — yeniden indirme yok. Pro için tam kaynak önceden önbellekte: zaman çizelgesinin her yerinde düzeltebilirsin.",
    "feat.refine.tag": "live",
    "feat.library.title": "Personal Library",
    "feat.library.body":
      "Üzerinde çalışmaya devam etmek istediğin klipleri kaydet. Free 14 gün boyunca 10 tane tutar; Pro süresiz 50 tane tutar.",
    "feat.library.tag": "live",
    "feat.large.title": "Her boyutta dosya",
    "feat.large.body":
      "Free'de 50 MB üzeri dosyalar — 24 saat geçerli kısa bir indirme bağlantısı. Pro, sohbette 2 GB'a kadar dosya alır.",
    "feat.large.tag": "live",
    "feat.langs.title": "Senin dilini konuşur",
    "feat.langs.body":
      "Bot 7 dilde gelir ve seninkini otomatik algılar. /lang ile istediğin zaman değiştir.",
    "feat.langs.tag": "live",
    "feat.precise.title": "Frame hassasiyetinde kesim",
    "feat.precise.body":
      "Yeniden kodlamayla 0,1 saniyeye kadar kırp. Klipler ve reels için mükemmel.",
    "feat.precise.tag": "pro",
    "feat.crop.title": "Kareyi kırp",
    "feat.crop.body":
      "Yatay videoları dikey platformlar için tek dokunuşla yeniden kullan.",
    "feat.crop.tag": "pro",
    "feat.recut.title": "Yeniden indirmeden yeniden kes",
    "feat.recut.body":
      "Sınırları anında değiştir. Kaynak oturum boyunca önbellekte kalır.",
    "feat.recut.tag": "pro",
    "feat.miniapp.title": "Mini App editörü",
    "feat.miniapp.body":
      "Telegram içinde zaman çizelgesi, scrubber ve önizlemeli görsel editör. Uygulama kurulumu yok.",
    "feat.miniapp.tag": "live",
    "feat.subs.title": "Telegram Stars ile Pro",
    "feat.subs.body":
      "Doğrudan sohbette Stars ile öde. Kart numarası yok, harici ödeme yok.",
    "feat.subs.tag": "pro",
    "feat.priority.title": "Öncelikli kuyruk",
    "feat.priority.body":
      "Yoğun saatlerde Pro kullanıcılar sıraya girmez. Ortalama bekleme 10 saniyenin altında kalır.",
    "feat.priority.tag": "soon",
    "feat.history.title": "Geçmiş ve kotalar",
    "feat.history.body":
      "Son işlerin, günlük limitler ve tek dokunuşla yeniden çalıştırma.",
    "feat.history.tag": "live",
    "tag.live": "kullanılabilir",
    "tag.pro": "Pro",
    "tag.soon": "yakında",
    "how.title": "Nasıl çalışır",
    "how.step1.title": "Bağlantı gönder",
    "how.step1.body": "@Clipra_bot ile sohbete herhangi bir video URL'si yapıştır.",
    "how.step2.title": "Ne yapacağını seç",
    "how.step2.body":
      "Tüm dosyayı indir veya bir parça için saniyeleri belirt.",
    "how.step3.title": "Dosyanı al",
    "how.step3.body":
      "Videoyu sohbette geri veririz — veya temiz bir indirme bağlantısı.",
    "cta.title": "Klip yapmaya hazır mısın?",
    "cta.body": "Başlamak ücretsiz. Kayıt yok. Telegram'ı aç, yeter.",
    "cta.button": "Botu başlat",
    "foot.tagline": "// küçük yapılmış video araçları",
    "foot.terms": "Kullanım Şartları",
    "foot.copyright": "© Clipra · Tüm hakları saklıdır",
  },

  pt: {
    "nav.lang": "Idioma",
    "nav.admin": "Admin",
    "nav.openBot": "Abrir bot do Telegram",
    "hero.eyebrow": "// ferramentas de vídeo · v1.0",
    "hero.title.line1": "Corte e reenquadre vídeos para shorts.",
    "hero.title.line2_prefix": "Em um único Telegram-",
    "hero.title.swap_a": "bot",
    "hero.title.swap_b": "app",
    "hero.title.line2_suffix": ".",
    "hero.subtitle":
      "Cole um link do seu próprio vídeo e transforme-o em um clipe vertical pronto para compartilhar. Recorte o momento que precisa e reenquadre para a tela do celular — tudo dentro do Telegram, sem apps para instalar.",
    "hero.cta.primary": "Abrir no Telegram",
    "hero.cta.secondary": "Como funciona",
    "feat.title": "O que o Clipra faz",
    "feat.subtitle": "Tudo abaixo já funciona ou está no roadmap público.",
    "feat.download.title": "Baixe vídeos por link",
    "feat.download.body":
      "Mais de 1.000 sites suportados. YouTube, Instagram Reels, TikTok, X, VK, Facebook, Vimeo e mais.",
    "feat.download.tag": "live",
    "feat.cut.title": "Corte um trecho por timecode",
    "feat.cut.body":
      "Diga ao bot os segundos exatos — você recebe só o clipe. Rápido, sem recodificação.",
    "feat.cut.tag": "live",
    "feat.refine.title": "Smart Refine — ajustes instantâneos",
    "feat.refine.body":
      "Mova as bordas do corte em 0,1 s e re-exporte em segundos — sem novo download. Pro tem a fonte completa em cache para refinar em qualquer ponto da timeline.",
    "feat.refine.tag": "live",
    "feat.library.title": "Personal Library",
    "feat.library.body":
      "Salve os clipes que quiser continuar ajustando. Free guarda 10 por 14 dias; Pro guarda 50 sem expiração.",
    "feat.library.tag": "live",
    "feat.large.title": "Arquivos de qualquer tamanho",
    "feat.large.body":
      "Arquivos acima de 50 MB no Free — um link de download curto válido por 24 horas. Pro recebe arquivos de até 2 GB direto no chat.",
    "feat.large.tag": "live",
    "feat.langs.title": "Fala o seu idioma",
    "feat.langs.body":
      "O bot vem em 7 idiomas e detecta o seu. Troque quando quiser com /lang.",
    "feat.langs.tag": "live",
    "feat.precise.title": "Corte preciso ao frame",
    "feat.precise.body":
      "Aparar até 0,1 s com recodificação. Perfeito para clipes e reels.",
    "feat.precise.tag": "pro",
    "feat.crop.title": "Recorte o frame",
    "feat.crop.body":
      "Transforme vídeos horizontais para plataformas verticais com um toque.",
    "feat.crop.tag": "pro",
    "feat.recut.title": "Recorte sem baixar de novo",
    "feat.recut.body":
      "Mude as bordas na hora. A fonte fica no cache durante a sessão.",
    "feat.recut.tag": "pro",
    "feat.miniapp.title": "Editor Mini App",
    "feat.miniapp.body":
      "Editor visual dentro do Telegram com timeline, scrubber e preview. Sem instalar app.",
    "feat.miniapp.tag": "live",
    "feat.subs.title": "Pro via Telegram Stars",
    "feat.subs.body":
      "Pague em Stars dentro do chat. Sem cartão, sem checkout externo.",
    "feat.subs.tag": "pro",
    "feat.priority.title": "Fila prioritária",
    "feat.priority.body":
      "Usuários Pro furam fila no horário de pico. Espera média abaixo de 10 segundos.",
    "feat.priority.tag": "soon",
    "feat.history.title": "Histórico e cotas",
    "feat.history.body":
      "Seus últimos trabalhos, limites diários e re-run com um toque.",
    "feat.history.tag": "live",
    "tag.live": "disponível",
    "tag.pro": "Pro",
    "tag.soon": "em breve",
    "how.title": "Como funciona",
    "how.step1.title": "Envie um link",
    "how.step1.body": "Cole qualquer URL de vídeo no chat com @Clipra_bot.",
    "how.step2.title": "Escolha o que fazer",
    "how.step2.body":
      "Baixe o arquivo completo ou indique os segundos para um trecho.",
    "how.step3.title": "Receba seu arquivo",
    "how.step3.body":
      "Devolvemos o vídeo no chat — ou um link de download limpo.",
    "cta.title": "Pronto pra clipar?",
    "cta.body": "Gratuito pra começar. Sem cadastro. Só abrir o Telegram.",
    "cta.button": "Iniciar o bot",
    "foot.tagline": "// ferramentas de vídeo, feitas pequenas",
    "foot.terms": "Termos de uso",
    "foot.copyright": "© Clipra · Todos os direitos reservados",
  },

  ru: {
    "nav.lang": "Язык",
    "nav.admin": "Админ",
    "nav.openBot": "Открыть бота в Telegram",
    "hero.eyebrow": "// видео-инструменты · v1.0",
    "hero.title.line1": "Режь и переформатируй видео под shorts.",
    "hero.title.line2_prefix": "В одном Telegram-",
    "hero.title.swap_a": "боте",
    "hero.title.swap_b": "app",
    "hero.title.line2_suffix": ".",
    "hero.subtitle":
      "Вставь ссылку на своё видео и преврати его в готовый вертикальный клип. Вырежи нужный момент, переформатируй под мобильный экран — всё внутри Telegram, без приложений.",
    "hero.cta.primary": "Открыть в Telegram",
    "hero.cta.secondary": "Как это работает",
    "feat.title": "Что умеет Clipra",
    "feat.subtitle": "",
    "feat.download.title": "Скачивает видео по ссылке",
    "feat.download.body":
      "1000+ сайтов. YouTube, Instagram Reels, TikTok, X, Facebook, Vimeo, VK и другие.",
    "feat.download.tag": "live",
    "feat.cut.title": "Режет фрагмент по таймкоду",
    "feat.cut.body":
      "Скажи боту секунды начала и конца — получишь только нужный фрагмент. Быстро, без внешних приложений и перекодирования.",
    "feat.cut.tag": "live",
    "feat.miniapp.title": "Редактор в Mini App",
    "feat.miniapp.body":
      "Визуальный редактор прямо в Telegram: таймлайн, лента кадров, превью.",
    "feat.miniapp.tag": "live",
    "feat.refine.title": "Smart Refine — мгновенные правки",
    "feat.refine.body":
      "Границы клипа можно редактировать и получать новый за секунды — без повторной загрузки.",
    "feat.refine.tag": "live",
    "feat.library.title": "Personal Library",
    "feat.library.body":
      "Сохраняй клипы, которые хочется повторно использовать. Бесплатно — до 10 на 14 дней, с Pro-подпиской — до 50 без ограничения срока.",
    "feat.library.tag": "live",
    "feat.history.title": "История загрузок",
    "feat.history.body":
      "Все задачи под рукой — превью, шеринг, редактирование, сохранение в Библиотеку. Бесплатно хранение до 5 дней, с Pro-подпиской — до 30.",
    "feat.history.tag": "live",
    "feat.langs.title": "Говорит на твоём языке",
    "feat.langs.body":
      "Mini App и бот доступны на 7 языках с автоопределением. Сменить — кнопкой в шапке.",
    "feat.langs.tag": "live",
    "feat.large.title": "Файлы любого размера",
    "feat.large.body":
      "Для файлов более 50 МБ бесплатно — короткая ссылка загрузки файла на 24 часа. С Pro-подпиской — файлы до 2 ГБ прямо в чат.",
    "feat.large.tag": "live",
    "feat.precise.title": "Точная нарезка до 0,1 сек",
    "feat.precise.body":
      "С перекодированием — для профессионально-идеальных клипов и мемов.",
    "feat.precise.tag": "pro",
    "feat.crop.title": "Crop кадра",
    "feat.crop.body":
      "Любое кадрирование видео. Пресеты 9:16 / 1:1 / 4:5 — для быстроты и удобства.",
    "feat.crop.tag": "pro",
    "feat.subs.title": "Pro через Telegram Stars",
    "feat.subs.body":
      "800 Stars в месяц, оплата прямо в чате. Без банковских карт, без внешних чекаутов, отменяется в один клик в настройках Telegram.",
    "feat.subs.tag": "pro",
    "tag.live": "live",
    "tag.pro": "Pro",
    "tag.soon": "скоро",
    "how.title": "Как это работает",
    "how.step1.title": "Отправь ссылку",
    "how.step1.body": "Вставь любую ссылку на видео в чат с @Clipra_bot.",
    "how.step2.title": "Выбери действие",
    "how.step2.body":
      "Открой Mini App для визуальной нарезки или просто напиши секунды для быстрого реза.",
    "how.step3.title": "Получи файл",
    "how.step3.body":
      "Возвращаем видео прямо в чат — или ссылку для скачивания, если файл большой.",
    "cta.title": "Готов резать?",
    "cta.body": "Бесплатно для старта. Без регистрации. Просто открой Telegram.",
    "cta.button": "Запустить бота",
    "foot.tagline": "// видео-инструменты, сделанные компактными",
    "foot.terms": "Условия использования",
    "foot.copyright": "© Clipra · Все права защищены",
  },
};

export function t(key, lang) {
  const code = STRINGS[lang] ? lang : DEFAULT_LANG;
  return STRINGS[code]?.[key] ?? STRINGS[DEFAULT_LANG][key] ?? key;
}
